import { useCallback, useEffect, useState } from 'react';
import classNames from 'classnames';
import styles from './Drawer.module.scss';
import { Heading, Icon } from 'ui/components';
import IconClose from 'ui/icons/icon-close-cross-website.svg';

export interface DrawerProps {
	className?: string;
	side: 'left' | 'right';
	title?: string;
	ariaClose?: string;
	children?: React.ReactNode;
	handleClose: () => void;
	isOpen?: boolean;
}

export const Drawer: React.FC<DrawerProps> = ({ className, title, ariaClose, children, handleClose, isOpen, side = 'right' }) => {
	// ESC to close (only when open)
	useEffect(() => {
		if (!isOpen) return;
		const onKeyDown = (e) => e.key === 'Escape' && handleClose?.();
		document.addEventListener('keydown', onKeyDown);
		return () => document.removeEventListener('keydown', onKeyDown);
	}, [isOpen, handleClose]);

	const drawerRef = useCallback(
		(node: HTMLDivElement | null) => {
			if (node) {
				if (!isOpen) {
					node.setAttribute('inert', '');
				} else {
					node.removeAttribute('inert');
				}
			}
		},
		[isOpen],
	);

	return (
		<div
			ref={drawerRef}
			className={classNames(styles.Drawer, styles[`Drawer___${isOpen ? 'open' : 'close'}`], {
				[styles[`Drawer___${side}`]]: side,
			})}
			aria-hidden={!isOpen}
			aria-labelledby={title ? 'drawer-title' : undefined}
		>
			<div className={classNames(styles.Drawer_overlay)} onClick={() => handleClose?.()} />
			<div
				className={classNames(styles.Drawer_panel, styles[`Drawer_panel___${isOpen ? 'open' : 'close'}`])}
				role="dialog"
				aria-modal={true}
				onClick={(e) => e.stopPropagation()} // don't close when clicking inside
			>
				{/* Close button */}
				<button className={classNames(styles.Drawer_close)} onClick={handleClose} aria-label={ariaClose}>
					<Icon size="md" className={classNames(styles.Drawer_closeIcon)}>
						<IconClose />
					</Icon>
				</button>
				{children}
			</div>
		</div>
	);
};
